import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Image as ImageIcon,
  Users,
  TrendingUp,
  LayoutDashboard,
  Edit3,
  Brush,
  BarChart3,
} from 'lucide-react';
import Sidebar from '../../components/sidebar/Sidebar';
import { logout, setUser } from '../../redux/auth/authSlice';
import { fetchUsageStatistics } from '../../redux/user/userSlice';
import { RootState } from '../../redux/store';
import chatService from '../../services/api/chatService';
import userService from '../../services/api/userService';
import aiAgentService from '../../services/api/aiAgentService';
import { OnboardingModal } from '../../components/onboarding';
import { Chat } from '../../types';
import { AgentWithIcon, FormattedChat } from '../../types/chat';
import { ResponsiveAppShell, TaskyButton, TaskyCard, TaskyStatCard, TaskyTopBar } from '../../components/tasky';

interface UserStats {
  total_messages: number;
  total_tokens: number;
  total_chats: number;
  remaining_tokens: number;
}

// Color palette for chat/agent avatars (rotation)
const AVATAR_PALETTE = [
  { bg: 'bg-primary/10', text: 'text-primary' },
  { bg: 'bg-purple/10', text: 'text-purple' },
  { bg: 'bg-success/10', text: 'text-success' },
  { bg: 'bg-pink/10', text: 'text-pink' },
  { bg: 'bg-warning/10', text: 'text-warning' },
];

function getAvatarColor(seed: number | string) {
  const n = typeof seed === 'number' ? seed : Array.from(String(seed)).reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_PALETTE[n % AVATAR_PALETTE.length];
}

function formatRelativeTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'たった今';
  if (diff < 3600) return `${Math.floor(diff / 60)}分前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}時間前`;
  if (diff < 172800) return '昨日';
  if (diff < 604800) return `${Math.floor(diff / 86400)}日前`;
  return d.toLocaleDateString('ja-JP');
}

const DashboardPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userChats, setUserChats] = useState<Chat[]>([]);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [agents, setAgents] = useState<AgentWithIcon[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgentWithIcon | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const { user } = useSelector((state: RootState) => state.auth);
  const { usageStatistics } = useSelector((state: RootState) => state.user);

  useEffect(() => {
    if (user && user.onboarding_completed === false) {
      setShowOnboarding(true);
    }
  }, [user]);

  const handleOnboardingComplete = useCallback(async () => {
    try {
      const response = await userService.completeOnboarding();
      if (response.success && response.user) {
        dispatch(setUser(response.user));
      }
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    } finally {
      setShowOnboarding(false);
    }
  }, [dispatch]);

  const handleOnboardingClose = useCallback(() => {
    setShowOnboarding(false);
  }, []);

  useEffect(() => {
    const loadDashboard = async () => {
      setIsLoading(true);
      try {
        await dispatch(fetchUsageStatistics());
        const [chatsRes, agentsRes, statsRes] = await Promise.allSettled([
          chatService.getUserChats(),
          aiAgentService.getActiveAgents(),
          user?.id ? userService.getUserStats(user.id) : Promise.resolve(null),
        ]);
        if (chatsRes.status === 'fulfilled' && Array.isArray(chatsRes.value)) {
          setUserChats(chatsRes.value as Chat[]);
        }
        if (agentsRes.status === 'fulfilled' && Array.isArray(agentsRes.value)) {
          setAgents(agentsRes.value as AgentWithIcon[]);
        }
        if (statsRes.status === 'fulfilled' && statsRes.value) setUserStats(statsRes.value as UserStats);
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadDashboard();
  }, [dispatch, user?.id]);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  const handleAgentSelect = (agentId: number) => {
    const agent = agents.find((a) => a.id === agentId) || null;
    setSelectedAgent(agent);
    if (agent) navigate(`/chat/ai?agent=${agentId}`);
  };

  const formattedChats: FormattedChat[] = userChats.map((chat) => ({
    id: chat.id,
    title: chat.title || chat.agent?.title || `Chat #${chat.id}`,
  }));

  const formattedAgents = agents.map((agent) => ({
    id: agent.id,
    title: agent.name || agent.title,
    description: agent.description || '',
    model_type: agent.model_type || 'chatgpt',
    prompt_template: agent.prompt_template || '',
    is_active: agent.is_active !== undefined ? agent.is_active : true,
    base_color: agent.base_color || '#FFC107',
    category: agent.category || 'Other',
    name: agent.name,
    avatar: agent.avatar,
    capabilities: agent.capabilities || [],
  }));

  const displayName = (() => {
    if (user?.name && user.name !== '未設定' && user.name !== '名称未設定') return user.name;
    if (user?.email) return user.email.split('@')[0];
    return 'ユーザー';
  })();

  const recentChats = userChats.slice(0, 5);
  const favoriteAgents = agents.filter((a) => (a as any).is_favorited).slice(0, 3);
  const displayAgents = favoriteAgents.length > 0 ? favoriteAgents : agents.slice(0, 3);

  const stat1 = userStats?.total_chats ?? userChats.length ?? 0;
  const stat2 = usageStatistics?.api_calls ?? 0;
  const stat3 = agents.length ?? 0;
  const stat4Remaining = usageStatistics?.remaining ?? userStats?.remaining_tokens ?? 0;

  const sidebar = (
    <Sidebar
      onLogout={handleLogout}
      chats={formattedChats}
      agents={formattedAgents}
      onSelectAgent={handleAgentSelect}
      selectedAgentId={selectedAgent?.id || null}
      onToggleCollapse={setSidebarCollapsed}
      collapsed={sidebarCollapsed}
    />
  );

  const mobileSidebar = (
    <Sidebar
      onLogout={handleLogout}
      chats={formattedChats}
      agents={formattedAgents}
      onSelectAgent={handleAgentSelect}
      selectedAgentId={selectedAgent?.id || null}
    />
  );

  return (
    <ResponsiveAppShell sidebar={sidebar} mobileSidebar={mobileSidebar} topbarTitle="ダッシュボード">
      <OnboardingModal
        isOpen={showOnboarding}
        userName={displayName}
        onComplete={handleOnboardingComplete}
        onClose={handleOnboardingClose}
      />

      <div className="flex min-h-[calc(100dvh-56px)] flex-col bg-warm-bg lg:h-screen lg:min-h-0">
        {/* Header */}
        <TaskyTopBar title="ダッシュボード" icon={<LayoutDashboard className="w-5 h-5" />} />

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden bg-white">
          <div className="max-w-6xl mx-auto px-4 py-5 sm:px-6 sm:py-6">
            {/* Welcome Banner */}
            <TaskyCard variant="warm" padding="lg" className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-dark">おかえりなさい、{displayName}さん</h2>
              </div>
              <TaskyButton
                onClick={() => navigate('/chat/ai', { state: { newChat: Date.now() } })}
                size="sm"
                className="w-full whitespace-nowrap sm:w-auto"
              >
                新しいチャットを始める
              </TaskyButton>
            </TaskyCard>

            {/* Stats Grid (4 cards) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              <TaskyStatCard
                icon={<MessageSquare />}
                iconTone="primary"
                label="チャット数"
                value={stat1.toLocaleString?.() ?? stat1}
              />
              <TaskyStatCard
                icon={<ImageIcon />}
                iconTone="purple"
                label="API呼び出し"
                value={stat2.toLocaleString?.() ?? stat2}
              />
              <TaskyStatCard
                icon={<Users />}
                iconTone="warning"
                label="エージェント"
                value={stat3.toLocaleString?.() ?? stat3}
              />
              <TaskyStatCard
                icon={<TrendingUp />}
                iconTone="success"
                label="残りトークン"
                value={(stat4Remaining as number).toLocaleString?.() ?? stat4Remaining}
              />
            </div>

            {/* Two-column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
              {/* Left: 最近のチャット */}
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-dark">最近のチャット</h3>
                  <TaskyButton
                    variant="link"
                    size="sm"
                    onClick={() => navigate('/chat/ai')}
                  >
                    すべて表示
                  </TaskyButton>
                </div>
                <div className="space-y-1">
                  {isLoading && (
                    <p className="text-sm text-gray-400 px-3 py-2">読み込み中...</p>
                  )}
                  {!isLoading && recentChats.length === 0 && (
                    <p className="text-sm text-gray-400 px-3 py-2">まだチャット履歴がありません</p>
                  )}
                  {recentChats.map((chat, idx) => {
                    const color = getAvatarColor(chat.id);
                    const agentTitle = chat.agent?.title || 'AI';
                    const firstChar = agentTitle.charAt(0);
                    return (
                      <button
                        key={chat.id}
                        type="button"
                        onClick={() => navigate(`/chat/${chat.id}`)}
                        className="w-full text-left flex flex-col gap-2 rounded-lg p-3 hover:bg-warm-surface transition-colors sm:flex-row sm:items-center sm:gap-3"
                      >
                        <div className={`w-8 h-8 rounded-full ${color.bg} flex items-center justify-center ${color.text} text-xs font-semibold flex-shrink-0`}>
                          {firstChar}
                        </div>
                        <div className="min-w-0 sm:flex-shrink-0 sm:w-[120px]">
                          <span className="text-sm font-medium text-gray-700 truncate block">{agentTitle}</span>
                        </div>
                        <p className="text-sm text-gray-500 line-clamp-2 min-w-0 sm:truncate sm:flex-1">
                          {chat.title || chat.first_user_message || `Chat #${chat.id}`}
                        </p>
                        <span className="text-xs text-gray-400 flex-shrink-0">{formatRelativeTime(chat.created_at)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Right: お気に入りエージェント */}
              <div className="lg:col-span-1">
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-dark">お気に入りエージェント</h3>
                </div>
                <div className="space-y-3">
                  {isLoading && (
                    <p className="text-sm text-gray-400 px-3 py-2">読み込み中...</p>
                  )}
                  {!isLoading && displayAgents.length === 0 && (
                    <p className="text-sm text-gray-400 px-3 py-2">エージェントがありません</p>
                  )}
                  {displayAgents.map((agent, idx) => {
                    const color = getAvatarColor(agent.id ?? idx);
                    const AgentIcon = [Edit3, Brush, BarChart3][idx % 3];
                    return (
                      <button
                        key={agent.id}
                        type="button"
                        onClick={() => agent.id && handleAgentSelect(agent.id)}
                        className="w-full text-left bg-warm-surface rounded-xl p-4 hover:shadow-card transition-shadow cursor-pointer"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full ${color.bg} flex items-center justify-center flex-shrink-0`}>
                            <AgentIcon className={`w-5 h-5 ${color.text}`} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-700 truncate">{agent.name || agent.title}</p>
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                              {agent.description || 'AIアシスタント'}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResponsiveAppShell>
  );
};

interface StatCardProps {
  icon: React.ReactNode;
  bgIcon: string;
  label: string;
  value: number | string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, bgIcon, label, value }) => (
  <div className="bg-warm-surface rounded-xl p-5">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-full ${bgIcon} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-sm text-gray-500">{label}</p>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-dark">{value.toLocaleString?.() ?? value}</span>
        </div>
      </div>
    </div>
  </div>
);

export default DashboardPage;
